const en = {
  localeCode: "en-US",
  common: {
    add: "Add",
    all: "All",
    cancel: "Cancel",
    confirm: "Confirm",
    delete: "Delete",
    edit: "Edit",
    generated: "Generated",
    none: "None",
    reset: "Reset",
    restore: "Restore",
    save: "Save",
    saveAnyway: "Save anyway",
    view: "View"
  },
  shell: {
    expenseCommandCenter: "Expense command center",
    installApp: "Install app",
    logout: "Logout",
    mainNavigation: "Main navigation",
    personalFinance: "Personal finance",
    themeMode: "Theme mode",
    toggleNavigation: "Toggle navigation"
  },
  auth: {
    backToSignIn: "Back to sign in",
    confirmPassword: "Confirm password",
    createAccount: "Create account",
    createWorkspace: "Create a secure local workspace",
    email: "Email",
    fullName: "Full name",
    password: "Password",
    signIn: "Sign in",
    signInSubtitle: "Sign in to your finance workspace"
  },
  notifications: {
    accountCreated: "Account created.",
    actionFailed: "Action failed.",
    backupDownloaded: "Backup downloaded.",
    backupRestored: "Backup restored.",
    backupRestoreFailed: "Backup restore failed.",
    csvExported: "CSV exported.",
    expenseAdded: "Expense added.",
    expenseDeleted: "Expense deleted.",
    expenseDeleteFailed: "Failed to delete expense.",
    expenseRestored: "Expense restored.",
    expenseRestoreFailed: "Failed to restore expense.",
    expenseSaveFailed: "Failed to save expense.",
    expenseUpdated: "Expense updated.",
    expenseUpdateFailed: "Failed to update expense.",
    expensesLoadFailed: "Failed to load expenses.",
    expensesResetFailed: "Failed to reset expenses.",
    goalDeleted: "Goal deleted.",
    goalDeleteFailed: "Failed to delete goal.",
    goalReorderFailed: "Failed to reorder goals.",
    goalRestored: "Goal restored.",
    goalRestoreFailed: "Failed to restore goal.",
    goalSaved: "Goal saved.",
    goalSaveFailed: "Failed to save goal.",
    goalsLoadFailed: "Failed to load goals.",
    goalsResetFailed: "Failed to reset goals.",
    goalUpdated: "Goal updated.",
    goalUpdateFailed: "Failed to update goal.",
    importEmpty: "No valid expenses were found in this file.",
    importFailed: "Import failed.",
    appInstalled: "LedgerFlow is ready to open as an app.",
    installDismissed: "Install skipped. You can install LedgerFlow later.",
    jsonExported: "JSON exported.",
    localDataReset: "Local expenses and goals were reset.",
    passwordTooShort: "Password must be at least 8 characters.",
    passwordsDoNotMatch: "Passwords do not match.",
    profileRequired: "Name and email are required.",
    profileSaved: "Profile saved.",
    profileSaveFailed: "Profile could not be saved.",
    recurringExpensesAdded: (count) => `${count} recurring expenses were added.`,
    settingsSaved: "Settings saved.",
    settingsSaveFailed: "Failed to save settings.",
    themeSaveFailed: "Theme setting could not be saved.",
    undo: "UNDO",
    welcomeBack: "Welcome back.",
    expensesImported: (count) => `${count} expenses imported.`
  },
  dialogs: {
    deleteExpense: {
      confirmLabel: "Delete",
      message: "This expense will be removed. You can undo it for 5 seconds after deletion.",
      title: "Delete expense?"
    },
    deleteGoal: {
      confirmLabel: "Delete",
      message: "This goal will be removed. You can undo it for 5 seconds after deletion.",
      title: "Delete goal?"
    },
    resetWorkspace: {
      confirmLabel: "Reset",
      message: "This will delete all local expenses and goals for this user.",
      title: "Reset workspace?"
    },
    restoreBackup: {
      confirmLabel: "Restore",
      message: "This backup will replace your current local expenses, goals, and settings.",
      title: "Restore backup?"
    },
    saveBudget: {
      confirmLabel: "Save anyway",
      message: "Budget is lower than this month's spending. Save these settings anyway?",
      title: "Save budget?"
    }
  },
  dashboard: {
    activeGoals: "Active goals",
    add: "Add",
    addExpenses: "Add expenses",
    addExpense: "Add expense",
    addFirstExpense: "Add first expense",
    addExpensesToRenderChart: "Add expenses to render a chart.",
    barChart: "Bar chart",
    barsAppearAfterFirstCategory: "Bars appear after your first spending category.",
    budgetIsNotSet: "Budget is not set",
    budgetProgress: "Budget progress",
    budgetRemaining: (amount) => `${amount} remains in this month's budget.`,
    budgetReached: "Monthly budget is fully used.",
    budgetUsed: "Budget used",
    categoryBudgets: "Category budgets",
    categoriesAppear: "Categories appear once expenses are added.",
    categoryShare: "Category share",
    categoryTotals: "Category totals",
    expense: "Expense",
    financialDashboard: "Financial dashboard",
    firstTransactionHint: "Track your first transaction and the dashboard will come alive.",
    goals: "Goals",
    goalsHint: "Goals keep savings visible beside spending.",
    income: "Income",
    incomeMinusExpenses: "Income minus this month's expenses",
    latestOperations: "Latest operations",
    leftAmount: (amount) => `${amount} left`,
    limitsByCategory: "Limits by category",
    lineChart: "Line chart",
    livePulse: "Live financial pulse",
    monthlyHistoryHint: "Monthly history will build over time.",
    monthlyIncomePlan: "Monthly income plan",
    monthlyLimit: (amount) => `${amount} monthly limit`,
    monthlyPlan: "Monthly plan",
    noBarsYet: "No bars yet",
    noCategoryBudgets: "No category budgets",
    noCategoryData: "No category data",
    noData: "No data",
    noExpensesYet: "No expenses yet",
    noGoalsYet: "No goals yet",
    noTrendYet: "No trend yet",
    pieChart: "Pie chart",
    recentActivity: "Recent activity",
    recurringThisMonth: (count) => `${count} recurring this month`,
    savingsProgress: "Savings progress",
    savingsTargets: "Savings targets",
    savedAmount: (amount) => `${amount} saved`,
    spentAmount: (amount) => `${amount} spent`,
    setBudgetHint: "Set a monthly budget to track how much money remains.",
    setCategoryBudgetsHint: "Set category budgets in Settings to monitor limits.",
    sixMonthTrend: "Six-month trend",
    spendingMix: "Spending mix",
    topCategory: "Top category",
    totalBalance: "Total balance",
    transactions: "Transactions",
    transactionsCount: (count) => `${count} transactions`,
    valueOfLimit: (spent, limit) => `${spent} of ${limit}`,
    tuneBudget: "Tune budget"
  },
  expenses: {
    actions: "Actions",
    addExpense: "Add expense",
    addFirstExpense: "Add first expense",
    amountColumn: (currency) => `Amount (${currency})`,
    category: "Category",
    clearFilters: "Clear filters",
    csv: "CSV",
    date: "Date",
    endDate: "End date",
    excel: "Excel",
    expenses: "Expenses",
    import: "Import",
    json: "JSON",
    maxAmount: "Max amount",
    minAmount: "Min amount",
    name: "Name",
    needCleanSlate: "Need a clean slate?",
    noExpensesFound: "No expenses found",
    noExpensesYet: "No expenses yet",
    noMatches: "Nothing matches these filters yet.",
    pdf: "PDF",
    periodFilters: {
      all: "All",
      today: "Today",
      week: "Week",
      month: "Month",
      year: "Year"
    },
    priority: "Priority",
    receipt: "Receipt",
    repeat: "Repeat",
    resetAllLocalData: "Reset all local data",
    search: "Search",
    searchPlaceholder: "Search expenses",
    startDate: "Start date",
    tags: "Tags",
    transactions: "Transactions",
    firstExpenseHint: "Add your first transaction and LedgerFlow will start building analytics."
  },
  goals: {
    addGoal: "Add goal",
    createFirstGoal: "Create first goal",
    daysLeft: (days) => `${days} days left`,
    delete: "Delete",
    edit: "Edit",
    financialTargets: "Financial targets",
    goals: "Goals",
    noGoalsYet: "No goals yet",
    target: (date) => `Target: ${date}`,
    targetDatePassed: "Target date passed",
    emptyHint: "Create a target and track progress beside everyday spending."
  },
  reports: {
    addExpensesForReport: "Add expenses to populate the report.",
    breakdown: "Breakdown",
    categories: "Categories",
    comparison: "Comparison",
    largestSpendAreas: "Largest spend areas",
    monthTotals: "Month totals",
    monthlyReadout: "Monthly readout",
    monthlyTotalsHint: "Monthly totals will build over time.",
    noCategoriesYet: "No categories yet",
    noMonthlyTotalsYet: "No monthly totals yet",
    noPriorityDataYet: "No priority data yet",
    prioritiesHint: "Priorities appear after expenses are added.",
    priorityPressure: "Priority pressure",
    reports: "Reports"
  },
  settings: {
    accentColor: "Accent color",
    appearance: "Appearance",
    backupAndRestore: "Backup and restore",
    budgetAndIncome: "Budget and income",
    budgetsAndOrder: "Budgets and order",
    categoryLimits: "Category limits",
    currencies: "Currencies",
    dangerZone: "Danger zone",
    data: "Data",
    displayCurrency: "Display currency",
    downloadBackup: "Download backup",
    dragCategories: "Drag categories to reorder",
    editableExchangeRates: "Editable exchange rates",
    fontSize: "Font size",
    language: "Language",
    localeAndDisplay: "Locale and display",
    monthlyBudget: "Monthly budget",
    monthlyIncome: "Monthly income",
    plan: "Plan",
    preferences: "Preferences",
    ratePerUsd: (currency) => `${currency} per 1 USD`,
    ratesHint: "Rates are stored locally and used to convert each expense from its original currency into your display currency.",
    resetLocalData: "Reset local data",
    resetWorkspace: "Reset workspace",
    restoreBackup: "Restore backup",
    saveSettings: "Save settings",
    settings: "Settings",
    theme: "Theme",
    workspaceControls: "Workspace controls",
    dangerHint: "This removes all local expenses and goals for the signed-in user."
  },
  profile: {
    accountIdentity: "Account identity",
    details: "Details",
    email: "Email",
    firstSession: "First session",
    lastLogin: "Last login",
    name: "Name",
    personalInformation: "Personal information",
    profile: "Profile",
    saveProfile: "Save profile",
    uploadAvatar: "Upload avatar",
    yourProfile: "Your profile"
  },
  expenseForm: {
    addTitleAndAmount: "Add a title and a positive amount.",
    amount: (currency) => `Amount (${currency})`,
    cancel: "Cancel",
    category: "Category",
    date: "Date",
    description: "Description",
    editExpense: "Edit expense",
    name: "Name",
    namePlaceholder: "Groceries, rent, subscription...",
    newExpense: "New expense",
    optionalNote: "Optional note",
    priority: "Priority",
    receipt: "Receipt",
    receiptTooLarge: "Receipt must be smaller than 2 MB for local backup storage.",
    removeReceipt: (name) => `Remove ${name}`,
    repeat: "Repeat",
    save: "Save",
    tags: "Tags"
  },
  goalForm: {
    addTitleAndTarget: "Add a title and a positive target amount.",
    cancel: "Cancel",
    currentProgress: "Current progress",
    description: "Description",
    editGoal: "Edit goal",
    goalTitle: "Goal title",
    goalPlaceholder: "Vacation, new laptop, emergency fund",
    newGoal: "New goal",
    save: "Save",
    targetAmount: "Target amount",
    targetDate: "Target date",
    whyItMatters: "Why this target matters"
  },
  insights: {
    addExpensesPatterns: "Add expenses to see patterns.",
    budgetAlertsHint: "Set a monthly budget to unlock budget alerts.",
    budgetHealth: "Budget health",
    categoryAlerts: "Category alerts",
    categoryBudgetOk: "Category budgets are currently under control.",
    leftInBudget: (amount) => `${amount} left in the monthly budget.`,
    monthlyPace: "Monthly pace",
    newBaseline: "New baseline",
    newBaselineDetail: "This month will become your comparison point.",
    noData: "No data",
    notSet: "Not set",
    overBudget: (categories) => `${categories} over budget.`,
    recurringDetail: "Automatically generated and repeating expenses this month.",
    recurringLoad: "Recurring load",
    spendingAbove: "Spending is above last month.",
    spendingBelow: "Spending is below last month.",
    spentThisMonth: (amount) => `${amount} spent this month.`,
    topCategory: "Top category"
  },
  onboarding: {
    budgets: "Create budgets",
    getStarted: "Get Started",
    goals: "Reach your goals",
    intro: "A focused workspace for tracking spending, planning budgets, and building better financial habits.",
    title: "Welcome to LedgerFlow",
    track: "Track expenses"
  },
  options: {
    menu: {
      dashboard: "Dashboard",
      expenses: "Expenses",
      goals: "Goals",
      reports: "Reports",
      profile: "Profile",
      settings: "Settings"
    },
    categories: {
      "Food & Dining": "Food & Dining",
      Transportation: "Transportation",
      Housing: "Housing",
      Shopping: "Shopping",
      Entertainment: "Entertainment",
      Healthcare: "Healthcare",
      Other: "Other"
    },
    priorities: {
      Low: "Low",
      Medium: "Medium",
      High: "High",
      Critical: "Critical"
    },
    frequencies: {
      "One-time": "One-time",
      Daily: "Daily",
      Weekly: "Weekly",
      Monthly: "Monthly",
      Yearly: "Yearly"
    },
    tags: {
      Essential: "Essential",
      Business: "Business",
      Personal: "Personal",
      Family: "Family",
      Travel: "Travel",
      Subscription: "Subscription",
      Optional: "Optional"
    },
    themeModes: {
      auto: "Auto",
      light: "Light",
      dark: "Dark"
    },
    fontSizes: {
      compact: "Compact",
      comfortable: "Comfortable",
      large: "Large"
    },
    languages: {
      EN: "English",
      RU: "Russian",
      TR: "Turkish"
    },
    accentThemes: {
      "#2563eb": "Blue",
      "#0f766e": "Teal",
      "#0f8a5f": "Emerald",
      "#b7791f": "Amber",
      "#be123c": "Rose"
    }
  }
};

export default en;
